import { Space, Switch } from "antd";
import { AvatarShape } from "../../../../types";
import { useCallback, useMemo } from "react";
import { PaletteResponse } from "pages/media/types";
import { PlaceholderColorPicker } from "../../../controls/PlaceholderColorPicker";
import { removeOpacityFromHex } from "utils";
import { DEFAULT_STROKE_COLOR } from "../../../../constants";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { OpacitySlider } from "../../../controls/OpacitySlider";
import { getUpdatedItemPositionOnAddingStroke } from "../../../../helpers/tabHelpers";
import { Stroke } from "../../../controls/Stroke";
import {
  resolveColorToBrandKitPlaceholder,
  resolveColorToBrandkitColorIfNeeded,
  isGradient,
} from "../../../../helpers/itemsHelper";

export const AvatarStrokeFeature = ({
  brandKitPalette,
  previewWidth,
  previewHeight,
}: {
  brandKitPalette: PaletteResponse;
  previewWidth: number;
  previewHeight: number;
}) => {
  const { presenter, setPresenter } = useTemplateEditorStore((store) => ({
    presenter: store.templateState.presenter,
    setPresenter: store.setPresenter,
  }));

  const addTemplateUpdate = useTemplatesStatusStore(
    (state) => state.addTemplateUpdate
  );

  const avatarHasStroke = useMemo(
    () => !!presenter.stroke?.color && !!presenter.stroke.width,
    [presenter.stroke?.color, presenter.stroke?.width]
  );

  const handleStrokeWidthChange = (value: number | undefined) => {
    if (!value) {
      setPresenter({
        stroke: undefined,
      });
      addTemplateUpdate();
      return;
    }

    const valueRelativeToWidth = value / previewWidth;
    const { x, y } = getUpdatedItemPositionOnAddingStroke(
      value,
      presenter.position,
      presenter.size,
      previewWidth,
      previewHeight
    );
    setPresenter({
      stroke: {
        ...presenter.stroke,
        width: valueRelativeToWidth,
      },
      position: {
        x,
        y,
      },
    });

    addTemplateUpdate();
  };

  const handleStrokeToggle = useCallback(
    (checked: boolean) => {
      const background = resolveColorToBrandkitColorIfNeeded(
        presenter.background,
        brandKitPalette
      );
      const strokeColor = resolveColorToBrandKitPlaceholder(
        brandKitPalette.secondary_color ?? undefined,
        brandKitPalette
      );

      setPresenter({
        stroke: checked
          ? {
              ...presenter.stroke,
              color: strokeColor,
              width: 1 / previewWidth,
            }
          : undefined,
        // force a background when showing stroke (BE)
        ...(checked
          ? {
              background: isGradient(background)
                ? background
                : resolveColorToBrandKitPlaceholder(
                    removeOpacityFromHex(
                      background ??
                        (brandKitPalette.secondary_color ||
                          DEFAULT_STROKE_COLOR)
                    ),
                    brandKitPalette
                  ),
            }
          : null),
      });
      addTemplateUpdate();
    },
    [
      presenter.background,
      brandKitPalette,
      setPresenter,
      addTemplateUpdate,
      previewWidth,
      presenter.stroke,
    ]
  );

  const strokeWidth = Math.round((presenter.stroke?.width ?? 1) * previewWidth);

  return (
    <>
      {presenter.avatarShape !== AvatarShape.Regular && (
        <>
          <Switch
            checked={avatarHasStroke}
            onChange={handleStrokeToggle}
            style={{ position: "absolute", top: 10, right: 8 }}
            size="small"
          />
          {presenter.stroke?.color && !!presenter.stroke.width && (
            <Space size="small">
              <PlaceholderColorPicker
                shape="square"
                showText={false}
                value={presenter.stroke.color}
                palette={brandKitPalette}
                onClear={() => {
                  setPresenter({
                    stroke: undefined,
                  });
                  addTemplateUpdate();
                }}
                onChangeComplete={(resolvedColor) => {
                  if (typeof resolvedColor !== "string") return;
                  setPresenter({
                    stroke: {
                      ...presenter.stroke,
                      color: resolvedColor,
                    },
                  });
                  addTemplateUpdate();
                }}
                allowClear
                disabledAlpha
                triggerProps={{
                  $backgroundColor: "transparent",
                }}
              />
              <Stroke
                value={strokeWidth}
                onChange={handleStrokeWidthChange}
                max={10}
                min={1}
              />
              <OpacitySlider
                hexColor={presenter.stroke.color ?? undefined}
                palette={brandKitPalette}
                onChange={(hexColorWithOpacity) => {
                  setPresenter({
                    stroke: {
                      ...presenter.stroke,
                      color: hexColorWithOpacity,
                    },
                  });
                  addTemplateUpdate();
                }}
              />
            </Space>
          )}
        </>
      )}
    </>
  );
};
